import { Injectable, NgZone } from '@angular/core';
import { TelnyxRTC } from '@telnyx/webrtc';
import { DpUtilsService } from 'digitup-lib/dist/digitup-lib';
import { BehaviorSubject, Subject } from 'rxjs';
import { ConnectionLogService } from './connection-log.service';
import { RestService } from './rest.service';
import { UserSmall } from '@interfaces/user';
import { Journey } from '@interfaces/rest/interfaces/journey';

// La 'invitación' del SDK de Telnyx es más simple, la llamaremos 'Call'
export type TelnyxCall = any;

export interface CallState {
  id: string;
  call: TelnyxCall;
  state: 'ringing' | 'active' | 'onHold';
  callerId: string;
  callerName?: string;
  startTime?: Date;
  isMuted?: boolean;
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

@Injectable({
  providedIn: 'root'
})
export class SipService {
  public client!: TelnyxRTC;
  private calls: Map<string, CallState> = new Map();
  private activeCallId?: string;

  // BehaviorSubject para mantener el estado persistente
  readonly connectionState$ = new BehaviorSubject<ConnectionStatus>('disconnected');
  readonly incomingCall$ = new Subject<TelnyxCall>();
  readonly callState$ = new Subject<string>();
  readonly callsUpdate$ = new BehaviorSubject<CallState[]>([]);

  // 🔥 Gestión centralizada de datos de usuario por número de teléfono
  private userDataByNumber = new Map<string, { user?: UserSmall, journeys: Journey[] }>();
  private queriedNumbers = new Set<string>(); // Rastrear números ya consultados
  private loadingNumbers = new Set<string>(); // Rastrear números en proceso de carga
  readonly userDataUpdate$ = new BehaviorSubject<Map<string, { user?: UserSmall, journeys: Journey[] }>>(new Map());

  // Configuración centralizada
  private readonly defaultCredentials = {
    username: 'operator1',
    password: 'operator1'
  };

  private isAutoConnectEnabled = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectTimeout?: any;
  private keepaliveInterval?: any;
  private keepalivePongTimeout?: any;
  private lastPingTime?: number;
  private hasInternetConnection = true; // Flag para saber si hay internet real
  private lastSuccessfulConnection?: number; // Timestamp de última conexión exitosa
  private networkMetrics = {
    latency: 0,
    lastSuccessfulPing: new Date(),
    failedPings: 0,
    totalPings: 0,
    averageLatency: 0
  };

  constructor(
    private ngZone: NgZone,
    private utils: DpUtilsService,
    private logService: ConnectionLogService,
    private rest: RestService
  ) {
    // Inicializar el flag de internet con el estado real del navegador
    this.hasInternetConnection = typeof navigator !== 'undefined' ? navigator.onLine : true;
    console.log(`🌐 Estado inicial de internet: ${this.hasInternetConnection ? 'ONLINE' : 'OFFLINE'}`);

    // Monitoreo de visibilidad de página (detectar cuando se suspende la tab)
    this.setupPageVisibilityMonitoring();
  }

  /**
   * Monitorea cuando la página se oculta/muestra para detectar suspensiones
   */
  private setupPageVisibilityMonitoring() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          console.warn('⚠️ Página oculta - Posible suspensión del navegador');
          this.logService.logError('Página oculta - Navegador puede suspender conexión');
        } else {
          console.log('✅ Página visible nuevamente');
          // Verificar si la conexión sigue activa
          if (this.isAutoConnectEnabled && !this.client?.connected) {
            console.warn('⚠️ Conexión perdida mientras la página estaba oculta, reconectando...');
            // Cambiar estado a reconnecting antes de intentar
            this.connectionState$.next('reconnecting');
            this.attemptReconnect('page_visibility_restore');
          }
        }
      });

      // Monitorear eventos de red online/offline
      window.addEventListener('online', () => {
        console.log('✅ Navegador: Conexión a Internet restaurada');
        this.logService.logError('Internet restaurado');
        this.hasInternetConnection = true; // Actualizar flag

        const currentState = this.connectionState$.getValue();
        const hasActiveCalls = this.calls.size > 0;
        console.log(`🔍 Estado actual: ${currentState}, isAutoConnect: ${this.isAutoConnectEnabled}, client.connected: ${this.client?.connected}, hasInternet: ${this.hasInternetConnection}, llamadas activas: ${hasActiveCalls}`);

        if (!this.isAutoConnectEnabled) {
          console.warn('⚠️ Auto-conexión no está habilitada');
          return;
        }

        // Si el cliente está conectado, restaurar estado inmediatamente
        if (this.client?.connected) {
          console.log('✅ Cliente SIP conectado, restaurando estado a "connected"');
          this.connectionState$.next('connected');
          this.reconnectAttempts = 0;
          this.lastSuccessfulConnection = Date.now(); // 🔥 Actualizar timestamp de última conexión

          // Asegurar que keepalive está activo
          if (!this.keepaliveInterval) {
            console.log('💓 Reiniciando keepalive...');
            this.startKeepalive();
          }
          return;
        }

        // Si el cliente NO está conectado, intentar reconectar
        console.log('🔄 Cliente no conectado, intentando reconectar...');

        // Cancelar cualquier intento de reconexión anterior
        if (this.reconnectTimeout) {
          clearTimeout(this.reconnectTimeout);
          this.reconnectTimeout = undefined;
        }

        // Resetear intentos
        this.reconnectAttempts = 0;

        // Reconectar
        this.connectionState$.next('connecting');
        setTimeout(() => {
          this.autoConnect();
        }, 500);
      });

      window.addEventListener('offline', () => {
        console.warn('❌ Navegador: Sin conexión a Internet');
        this.logService.logError('Sin conexión a Internet');
        this.hasInternetConnection = false; // Actualizar flag

        // Cambiar estado inmediatamente a 'reconnecting' cuando se pierde internet
        // Esto asegura que la UI se bloquee y muestre el estado correcto
        if (this.connectionState$.getValue() === 'connected') {
          console.warn('⚠️ Conexión activa detectada, cambiando a reconnecting por pérdida de internet');
          this.connectionState$.next('reconnecting');
        }

        // El WebSocket detectará el cierre y disparará 'telnyx.socket.close'
        // que intentará reconectar automáticamente
      });
    }
  }

  // Keepalive mejorado: Mantener la conexión activa con ping real
  private startKeepalive() {
    this.stopKeepalive();
    console.log('💓 Iniciando keepalive mejorado (cada 25 segundos)');

    this.keepaliveInterval = setInterval(() => {
      if (this.client?.connected) {
        this.sendKeepalivePing();
      } else {
        console.warn('⚠️ Keepalive detectó que el cliente no está conectado');
        this.logService.logError('Keepalive detectó desconexión');
      }
    }, 25000); // Cada 25 segundos (antes de los típicos 30s de timeout)
  }

  private async sendKeepalivePing() {
    try {
      this.lastPingTime = Date.now();
      this.networkMetrics.totalPings++;

      console.log(`💓 Keepalive ping #${this.networkMetrics.totalPings}`);

      const clientAny = this.client as any;

      // Verificar estado de conexión sin modificar el estado
      const isConnected = this.client?.connected;
      const hasSession = clientAny?.sessionid;
      const currentState = this.connectionState$.getValue();

      console.log(`💓 Estado: connected=${isConnected}, sessionId=${hasSession ? 'presente' : 'ausente'}, currentState=${currentState}`);

      if (isConnected && hasSession) {
        // 📊 Solo logging - NO forzar cambio de estado
        if (currentState !== 'connected') {
          console.log(`📊 [INFO] Cliente conectado pero estado UI es '${currentState}' (puede estar reconectando)`);
        }

        // Conexión parece estar activa
        this.handlePongReceived();
      } else {
        throw new Error(`Conexión inactiva: connected=${isConnected}, sessionId=${hasSession ? 'presente' : 'ausente'}`);
      }

      // Timeout de 5 segundos para verificar estado
      this.keepalivePongTimeout = setTimeout(() => {
        this.networkMetrics.failedPings++;
        console.warn(`⚠️ Timeout de verificación (fallos: ${this.networkMetrics.failedPings})`);

        // Si fallan 3 verificaciones consecutivas, forzar reconexión
        if (this.networkMetrics.failedPings >= 3) {
          console.error('❌ 3 verificaciones fallidas consecutivas, forzando reconexión');
          this.logService.logError('Keepalive: 3 verificaciones fallidas consecutivas');
          if (this.isAutoConnectEnabled) {
            this.client?.disconnect();
          }
        }
      }, 5000);

    } catch (error) {
      console.error('❌ Error en keepalive ping:', error);
      this.networkMetrics.failedPings++;
      this.logService.logError('Error en keepalive ping', error);
    }
  }

  private handlePongReceived() {
    if (this.keepalivePongTimeout) {
      clearTimeout(this.keepalivePongTimeout);
      this.keepalivePongTimeout = undefined;
    }

    if (this.lastPingTime) {
      const latency = Date.now() - this.lastPingTime;
      this.networkMetrics.latency = latency;
      this.networkMetrics.lastSuccessfulPing = new Date();
      this.networkMetrics.failedPings = 0; // Reset fallos consecutivos

      // Calcular latencia promedio
      const totalPings = this.networkMetrics.totalPings;
      const avgLatency = this.networkMetrics.averageLatency;
      this.networkMetrics.averageLatency = (avgLatency * (totalPings - 1) + latency) / totalPings;

      console.log(`✅ Pong recibido - Latencia: ${latency}ms (Promedio: ${this.networkMetrics.averageLatency.toFixed(0)}ms)`);

      // Alertar si la latencia es muy alta
      if (latency > 1000) {
        console.warn(`⚠️ Latencia alta detectada: ${latency}ms`);
        this.logService.logError(`Latencia alta: ${latency}ms`);
      }
    }
  }

  private stopKeepalive() {
    if (this.keepaliveInterval) {
      clearInterval(this.keepaliveInterval);
      this.keepaliveInterval = undefined;
    }
    if (this.keepalivePongTimeout) {
      clearTimeout(this.keepalivePongTimeout);
      this.keepalivePongTimeout = undefined;
    }
  }

  async connect(username: string, password: string) {
    if (this.client?.connected) return;

    await this.utils.showLoading();
    this.client = new TelnyxRTC({ login: username, password: password });

    this.client.on('telnyx.ready', async () => this.ngZone.run(async () => {
      console.log('✅ SDK de Telnyx listo y conectado!');
      console.log('📊 Información de conexión:', {
        username: this.defaultCredentials.username,
        sessionId: (this.client as any)?.sessionid,
        connected: this.client?.connected
      });

      // Cambiar estado a connected solo cuando realmente esté listo
      this.connectionState$.next('connected');
      this.reconnectAttempts = 0;
      this.lastSuccessfulConnection = Date.now(); // 🔥 Guardar timestamp de conexión exitosa
      this.startKeepalive();
      this.logService.logConnection(this.defaultCredentials.username);

      await this.utils.hideLoading();
    }));

    this.client.on('telnyx.error', (error: any) => this.ngZone.run(async () => {
      console.error('❌ Error en el SDK de Telnyx:', error);

      // Manejar errores específicos de llamadas que ya no existen
      if (error?.message === 'CALL DOES NOT EXIST' || error?.code === -32002) {
        console.warn('⚠️ Llamada ya no existe en Telnyx, limpiando del estado local');

        // Buscar y eliminar la llamada del estado local
        if (error?.callID) {
          this.calls.delete(error.callID);
          if (this.activeCallId === error.callID) {
            this.activeCallId = undefined;
          }
          this.notifyCallsUpdate();
        }

        this.logService.logError(`Llamada no encontrada (${error.callID})`, error);
        // NO disparar reconexión para este tipo de error
        return;
      }

      this.stopKeepalive();
      this.logService.logError('Error en SDK de Telnyx', error);
      await this.utils.hideLoading();

      // Auto-reconexión si está habilitada
      if (this.isAutoConnectEnabled) {
        this.connectionState$.next('reconnecting');
        this.attemptReconnect('sdk_error', error);
      } else {
        this.connectionState$.next('disconnected');
        this.logService.logDisconnection('Error en SDK');
      }
    }));

    this.client.on('telnyx.socket.close', async (event: any) => this.ngZone.run(async () => {
      console.warn('⚠️ Socket WebSocket cerrado');

      // Capturar información detallada del cierre
      const closeInfo = {
        code: event?.code || 'desconocido',
        reason: event?.reason || 'sin razón especificada',
        wasClean: event?.wasClean !== undefined ? event.wasClean : 'desconocido',
        timestamp: new Date().toISOString(),
        reconnectAttempts: this.reconnectAttempts,
        clientConnected: this.client?.connected || false,
        currentState: this.connectionState$.getValue(),
        timeSinceLastConnection: this.lastSuccessfulConnection
          ? Date.now() - this.lastSuccessfulConnection
          : 'N/A'
      };

      console.error('🔍 DETALLES DEL CIERRE:', closeInfo);

      // Interpretación del código de cierre
      const codeInterpretation = this.interpretCloseCode(event?.code);
      console.warn(`📊 Interpretación: ${codeInterpretation}`);

      // 🔥 CRÍTICO: Si acabamos de conectar hace menos de 5 segundos, es un cierre del socket VIEJO
      // No necesitamos reconectar porque acabamos de establecer una conexión nueva
      const justConnected = this.lastSuccessfulConnection && (Date.now() - this.lastSuccessfulConnection) < 5000;

      if (justConnected) {
        console.log(`✅ Ignorando cierre de socket viejo (conectado hace ${Date.now() - this.lastSuccessfulConnection!}ms)`);

        // Asegurar que el estado esté en 'connected'
        const currentState = this.connectionState$.getValue();
        if (currentState !== 'connected') {
          console.log(`🔄 Corrigiendo estado de ${currentState} a 'connected'`);
          this.connectionState$.next('connected');
          this.reconnectAttempts = 0;
        }

        // Asegurar que keepalive está activo
        if (!this.keepaliveInterval) {
          console.log('💓 Reiniciando keepalive después de cierre de socket viejo...');
          this.startKeepalive();
        }

        return; // No hacer nada más, acabamos de conectar
      }

      // También verificar si el cliente está conectado (por si acaso)
      if (this.client?.connected) {
        console.log('✅ Cliente ya está conectado, ignorando cierre de socket');

        const currentState = this.connectionState$.getValue();
        if (currentState !== 'connected') {
          console.log(`🔄 Corrigiendo estado de ${currentState} a 'connected'`);
          this.connectionState$.next('connected');
          this.reconnectAttempts = 0;
        }

        if (!this.keepaliveInterval) {
          this.startKeepalive();
        }

        return;
      }

      this.stopKeepalive();
      this.logService.logError(`WebSocket cerrado - Código: ${closeInfo.code}, Razón: ${closeInfo.reason}, Interpretación: ${codeInterpretation}`, closeInfo);
      await this.utils.hideLoading();

      // Auto-reconexión si está habilitada
      if (this.isAutoConnectEnabled) {
        this.connectionState$.next('reconnecting');
        this.attemptReconnect(`socket_close (code: ${closeInfo.code})`);
      } else {
        this.connectionState$.next('disconnected');
        this.logService.logDisconnection(`Socket WebSocket cerrado (código: ${closeInfo.code})`);
      }
    }));

    // La 'invitación' del SDK de Telnyx es más simple, la llamaremos 'Call'
    this.client.on('telnyx.notification', (notification: any) => {
      this.ngZone.run(() => {
        if (notification.type !== 'callUpdate') return;

        const call = notification.call;
        console.log(`🔔 Notificación de llamada: ID=${call.id}, Estado=${call.state}`);
        this.callState$.next(call.state); // Notificamos el estado a la UI

        switch (call.state) {
          case 'new':
            // Estado inicial cuando se crea/recrea una llamada
            console.log(`📞 Nueva llamada creada/recreada: ID=${call.id}`);

            // Si la llamada ya existe (reconexión), actualizar el objeto call
            const existingNewCall = this.calls.get(call.id);
            if (existingNewCall) {
              console.log('✅ Llamada existente detectada, actualizando objeto call...');
              existingNewCall.call = call; // Actualizar referencia del objeto call
              this.notifyCallsUpdate();
            } else {
              // Es una nueva llamada que no conocemos, crear entrada
              console.log('📞 Creando nueva entrada para llamada');
              const callerId = call.options?.remoteCallerNumber || call.options?.remoteCallerName || 'Desconocido';
              const newCallState: CallState = {
                id: call.id,
                call: call,
                state: 'ringing',
                callerId: callerId,
                callerName: call.options?.remoteCallerName
              };
              this.calls.set(call.id, newCallState);
              this.incomingCall$.next(call);
              this.notifyCallsUpdate();

              // 🔥 Obtener datos de usuario automáticamente para llamadas entrantes
              if (callerId !== 'Desconocido') {
                this.fetchUserDataForCall(callerId);
              }
            }
            break;

          case 'answering':
            // Llamada en proceso de ser contestada
            console.log(`📞 Llamada siendo contestada: ID=${call.id}`);
            const answeringCall = this.calls.get(call.id);
            if (answeringCall) {
              answeringCall.call = call; // Actualizar referencia
              // Mantener el estado actual, no cambiar a ringing
              this.notifyCallsUpdate();
            }
            break;

          case 'ringing':
            // Añadir nueva llamada entrante
            // Logging completo de todas las opciones disponibles
            console.log('🔍 DEBUGGING - Información completa de la llamada:', {
              'call.options': call.options,
              'call.direction': call.direction,
              'call.peers': call.peers,
              'call.remoteSdp': call.remoteSdp ? 'presente' : 'ausente',
              'call completo': call
            });

            // Intentar obtener el número en diferentes formatos
            const callerId = call.options.remoteCallerNumber || call.options.remoteCallerName || 'Desconocido';
            console.log(`📞 Nueva llamada entrante:`, {
              id: call.id,
              callerId: callerId,
              direction: call.direction,
              peers: call.peers,
              remoteSdp: call.remoteSdp ? 'presente' : 'ausente'
            });

            const callState: CallState = {
              id: call.id,
              call: call,
              state: 'ringing',
              callerId: callerId,
              callerName: call.options.remoteCallerName
            };
            this.calls.set(call.id, callState);
            this.incomingCall$.next(call);
            this.notifyCallsUpdate();

            // 🔥 Obtener datos de usuario automáticamente para llamadas entrantes
            if (callerId !== 'Desconocido') {
              this.fetchUserDataForCall(callerId);
            }
            break;

          case 'active':
            // La llamada está activa
            console.log('✅ Llamada en estado ACTIVE. Adjuntando audio...');
            const existingCall = this.calls.get(call.id);
            if (existingCall) {
              existingCall.state = 'active';
              existingCall.call = call; // Actualizar referencia del objeto call
              existingCall.startTime = existingCall.startTime || new Date(); // Mantener tiempo original si existe
              this.activeCallId = call.id;

              const audioEl = document.getElementById('remoteAudio') as HTMLAudioElement;
              if (audioEl) {
                this.attachRemoteAudio(call, audioEl);
              } else {
                console.error("No se encontró el elemento #remoteAudio para adjuntar el audio.");
              }

              this.notifyCallsUpdate();
            } else {
              console.warn('⚠️ Llamada activa sin entrada previa, creándola...');
              const callerId = call.options?.remoteCallerNumber || call.options?.remoteCallerName || 'Desconocido';
              const newActiveCallState: CallState = {
                id: call.id,
                call: call,
                state: 'active',
                callerId: callerId,
                callerName: call.options?.remoteCallerName,
                startTime: new Date()
              };
              this.calls.set(call.id, newActiveCallState);
              this.activeCallId = call.id;

              const audioEl = document.getElementById('remoteAudio') as HTMLAudioElement;
              if (audioEl) {
                this.attachRemoteAudio(call, audioEl);
              }

              this.notifyCallsUpdate();
            }
            break;

          case 'hangup':
            // No eliminar aún, solo marcar para logging
            console.log(`📞 Evento hangup recibido: ID=${call.id}`);
            break;

          case 'destroy':
            // Eliminar la llamada del mapa inmediatamente
            console.log(`📞 Evento destroy recibido: ID=${call.id}, eliminando del mapa`);
            this.calls.delete(call.id);
            if (this.activeCallId === call.id) {
              this.activeCallId = undefined;
            }
            this.notifyCallsUpdate();
            console.log(`📞 Llamadas restantes: ${this.calls.size}`);
            break;
        }
      });
    });

    this.client.connect();
    this.connectionState$.next('connecting');
  }

  // Notificar actualización de llamadas
  private notifyCallsUpdate() {
    const callsArray = Array.from(this.calls.values());
    this.callsUpdate$.next(callsArray);
  }

  // El método answer ahora acepta un ID de llamada
  async answer(callId: string) {
    const callState = this.calls.get(callId);
    if (!callState) {
      console.warn('No se encontró la llamada con ID:', callId);
      return;
    }

    // Si hay una llamada activa, ponerla en espera
    if (this.activeCallId && this.activeCallId !== callId) {
      await this.holdCall(this.activeCallId);
    }

    try {
      await callState.call.answer();
      console.log('✅ Comando de Answer enviado para llamada:', callId);

      // 🔊 Automáticamente quitar el mute al contestar una llamada
      if (callState.isMuted) {
        await this.unmuteCall(callId);
      }
    } catch (error: any) {
      console.error('❌ Error al contestar llamada:', error);

      // Si la llamada ya no existe, limpiar del estado local
      if (error?.message === 'CALL DOES NOT EXIST' || error?.code === -32002) {
        console.warn('⚠️ La llamada ya no existe, limpiando del estado local');
        this.calls.delete(callId);
        if (this.activeCallId === callId) {
          this.activeCallId = undefined;
        }
        this.notifyCallsUpdate();
      }
    }
  }

  attachRemoteAudio(call: TelnyxCall, audioEl: HTMLAudioElement) {
    if (!call || !call.remoteStream) {
      console.error("Error crítico: El estado es 'active' pero remoteStream no está disponible.");
      return;
    }
    console.log("🔊 Adjuntando stream a elemento de audio:", call.remoteStream);
    audioEl.srcObject = call.remoteStream;
    audioEl.play().catch(e => console.error("Fallo de autoplay", e));
  }

  // Método para poner una llamada en espera
  async holdCall(callId: string) {
    const callState = this.calls.get(callId);
    if (!callState) {
      console.warn('No se encontró la llamada con ID:', callId);
      return;
    }

    if (callState.state === 'active') {
      try {
        await callState.call.hold();
        callState.state = 'onHold';
        if (this.activeCallId === callId) {
          this.activeCallId = undefined;
        }
        this.notifyCallsUpdate();
        console.log('📞 Llamada puesta en espera:', callId);
      } catch (error) {
        console.error('Error al poner llamada en espera:', error);
      }
    }
  }

  // Método para reanudar una llamada
  async resumeCall(callId: string) {
    const callState = this.calls.get(callId);
    if (!callState) {
      console.warn('No se encontró la llamada con ID:', callId);
      return;
    }

    // Si hay una llamada activa, ponerla en espera
    if (this.activeCallId && this.activeCallId !== callId) {
      await this.holdCall(this.activeCallId);
    }

    if (callState.state === 'onHold') {
      try {
        await callState.call.unhold();
        callState.state = 'active';
        this.activeCallId = callId;

        // Reattach audio
        const audioEl = document.getElementById('remoteAudio') as HTMLAudioElement;
        if (audioEl) {
          this.attachRemoteAudio(callState.call, audioEl);
        }

        this.notifyCallsUpdate();
        console.log('📞 Llamada reanudada:', callId);
      } catch (error) {
        console.error('Error al reanudar llamada:', error);
      }
    }
  }

  // Método para silenciar el micrófono (mute)
  async muteCall(callId: string) {
    const callState = this.calls.get(callId);
    if (!callState) {
      console.warn('No se encontró la llamada con ID:', callId);
      return;
    }

    try {
      await callState.call.muteAudio();
      callState.isMuted = true;
      this.notifyCallsUpdate();
      console.log('🔇 Micrófono silenciado para llamada:', callId);
    } catch (error) {
      console.error('Error al silenciar micrófono:', error);
    }
  }

  // Método para activar el micrófono (unmute)
  async unmuteCall(callId: string) {
    const callState = this.calls.get(callId);
    if (!callState) {
      console.warn('No se encontró la llamada con ID:', callId);
      return;
    }

    try {
      await callState.call.unmuteAudio();
      callState.isMuted = false;
      this.notifyCallsUpdate();
      console.log('🔊 Micrófono activado para llamada:', callId);
    } catch (error) {
      console.error('Error al activar micrófono:', error);
    }
  }

  // Método para alternar mute/unmute
  async toggleMute(callId: string) {
    const callState = this.calls.get(callId);
    if (!callState) {
      console.warn('No se encontró la llamada con ID:', callId);
      return;
    }

    if (callState.isMuted) {
      await this.unmuteCall(callId);
    } else {
      await this.muteCall(callId);
    }
  }

  async hangup(callId: string) {
    const callState = this.calls.get(callId);
    if (!callState) {
      console.warn('No hay llamada con ID:', callId);
      return;
    }

    console.log('📞 Detalles de la llamada antes de hangup:', {
      callId: callId,
      state: callState.state,
      callerId: callState.callerId,
      callObject: callState.call,
      direction: callState.call.direction,
      peers: callState.call.peers,
      options: callState.call.options
    });

    try {
      // Para llamadas que no hemos contestado, intentar con diferentes causas
      if (callState.state === 'ringing') {
        // Busy: indica que estamos ocupados
        // Rejected: rechazamos la llamada explícitamente
        console.log('📞 Intentando colgar llamada no contestada con causa BUSY...');
        await callState.call.hangup({ cause: 'BUSY', causeCode: 486 });
      } else {
        // Para llamadas activas, hangup normal
        await callState.call.hangup();
      }
      console.log(`📞 Comando hangup enviado para llamada (${callState.state}):`, callId);
      // No eliminamos aquí, dejamos que el evento 'hangup'/'destroy' de Telnyx lo haga
    } catch (error: any) {
      console.error('❌ Error al colgar llamada:', error);

      // Si es un error de "CALL DOES NOT EXIST", eliminar del estado local
      if (error?.message === 'CALL DOES NOT EXIST' || error?.code === -32002) {
        console.warn('⚠️ La llamada ya no existe, limpiando del estado local');
        this.calls.delete(callId);
        if (this.activeCallId === callId) {
          this.activeCallId = undefined;
        }
        this.notifyCallsUpdate();
      } else {
        // Si hay error diferente, eliminar de todos modos
        this.calls.delete(callId);
        if (this.activeCallId === callId) {
          this.activeCallId = undefined;
        }
        this.notifyCallsUpdate();
      }
    }
  }

  // Obtener todas las llamadas
  getCalls(): CallState[] {
    return Array.from(this.calls.values());
  }

  // Obtener una llamada específica
  getCall(callId: string): CallState | undefined {
    return this.calls.get(callId);
  }

  // Obtener la llamada activa
  getActiveCall(): CallState | undefined {
    return this.activeCallId ? this.calls.get(this.activeCallId) : undefined;
  }

  /**
   * Interpreta los códigos de cierre del WebSocket según RFC 6455
   */
  private interpretCloseCode(code: number): string {
    const interpretations: { [key: number]: string } = {
      1000: 'Cierre normal (1000) - Conexión completada exitosamente',
      1001: 'Going Away (1001) - Servidor apagándose o navegador navegando fuera de la página',
      1002: 'Protocol Error (1002) - Error en el protocolo WebSocket',
      1003: 'Unsupported Data (1003) - Tipo de dato no soportado',
      1005: 'No Status (1005) - No se proporcionó código de estado (anormal)',
      1006: 'Abnormal Closure (1006) - Cierre anormal sin Close frame (PROBLEMA DE RED/TIMEOUT)',
      1007: 'Invalid Data (1007) - Datos inconsistentes recibidos',
      1008: 'Policy Violation (1008) - Violación de política del servidor',
      1009: 'Message Too Big (1009) - Mensaje demasiado grande',
      1010: 'Mandatory Extension (1010) - Cliente esperaba una extensión',
      1011: 'Internal Server Error (1011) - Error interno del servidor',
      1012: 'Service Restart (1012) - Servidor reiniciando',
      1013: 'Try Again Later (1013) - Servidor temporalmente sobrecargado',
      1014: 'Bad Gateway (1014) - Gateway/proxy malo',
      1015: 'TLS Handshake (1015) - Fallo en handshake TLS'
    };

    const interpretation = interpretations[code];
    if (interpretation) {
      return interpretation;
    } else if (code >= 3000 && code <= 3999) {
      return `Código personalizado de librería (${code})`;
    } else if (code >= 4000 && code <= 4999) {
      return `Código personalizado de aplicación (${code})`;
    } else {
      return `Código desconocido (${code})`;
    }
  }

  async disconnect() {
    if (this.client) {
      await this.utils.showLoading();
      this.stopKeepalive();
      this.client.disconnect();
      // Limpiar datos de usuario al desconectar
      this.clearUserData();
      await this.utils.hideLoading();
    }
  }

  // Método de reconexión mejorado
  private attemptReconnect(reason: string, error?: any) {
    // Limpiar timeout anterior si existe
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectAttempts++;
    console.log(`🔄 Intento de reconexión ${this.reconnectAttempts}/${this.maxReconnectAttempts} (Razón: ${reason})`);
    this.logService.logReconnection(this.reconnectAttempts, this.maxReconnectAttempts);

    if (this.reconnectAttempts > this.maxReconnectAttempts) {
      console.error('❌ Máximo de intentos de reconexión alcanzado');
      this.connectionState$.next('disconnected');
      this.reconnectAttempts = 0;
      this.logService.logError('Máximo de intentos de reconexión alcanzado');
      this.utils.showToast({
        message: 'No se pudo reconectar al sistema de llamadas. Por favor, recarga la página.',
        cssClass: 'danger',
        duration: 10000
      });
      return;
    }

    // Calcular delay exponencial: 2s, 4s, 8s, 16s... máximo 30s
    const delay = Math.min(2000 * Math.pow(2, this.reconnectAttempts - 1), 30000);
    console.log(`⏳ Esperando ${delay}ms antes de reconectar...`);

    this.reconnectTimeout = setTimeout(() => {
      console.log('🔌 Intentando reconexión...');
      this.autoConnect();
    }, delay);
  }

  // Métodos para manejo centralizado de conexión

  /**
   * Inicializa la auto-conexión al cargar el servicio
   * Llamado desde app.component
   */
  initializeAutoConnect() {
    // Verifica si debe auto-conectar (puedes agregar lógica para verificar localStorage, etc.)
    const shouldAutoConnect = localStorage.getItem('sip_auto_connect') === 'true';
    if (shouldAutoConnect) {
      this.enableAutoConnect();
    }
  }

  /**
   * Habilita la auto-conexión
   */
  async enableAutoConnect() {
    this.isAutoConnectEnabled = true;
    localStorage.setItem('sip_auto_connect', 'true');
    await this.autoConnect();
  }

  /**
   * Deshabilita la auto-conexión
   */
  async disableAutoConnect() {
    this.isAutoConnectEnabled = false;
    localStorage.setItem('sip_auto_connect', 'false');
    await this.disconnect();
  }

  /**
   * Conecta automáticamente usando las credenciales por defecto
   */
  private async autoConnect() {
    if (this.connectionState$.getValue() === 'connected' || this.connectionState$.getValue() === 'connecting') {
      return;
    }

    try {
      await this.connect(this.defaultCredentials.username, this.defaultCredentials.password);
    } catch (error) {
      console.error('Error en auto-conexión SIP:', error);
    }
  }

  /**
   * Método para toggle manual de conexión (usado desde la UI)
   */
  async toggleConnection() {
    const currentState = this.connectionState$.getValue();
    if (currentState === 'connected') {
      await this.disableAutoConnect();
    } else {
      await this.enableAutoConnect();
    }
  }

  /**
   * Obtiene el estado actual de conexión
   */
  getCurrentConnectionState(): ConnectionStatus {
    return this.connectionState$.getValue();
  }

  /**
   * Verifica si la auto-conexión está habilitada
   */
  isAutoConnectActive(): boolean {
    return this.isAutoConnectEnabled;
  }

  /**
   * Obtiene información de diagnóstico sobre la conexión actual
   */
  getDiagnosticInfo() {
    if (!this.client) {
      return {
        connected: false,
        message: 'Cliente SIP no inicializado',
        networkMetrics: this.networkMetrics
      };
    }

    const clientAny = this.client as any;

    // Información adicional del navegador y conexión
    const connectionInfo = this.getConnectionInfo();

    return {
      connected: this.client.connected,
      username: this.defaultCredentials.username,
      sessionId: clientAny.sessionid,
      activeCalls: this.calls.size,
      activeCallId: this.activeCallId,
      calls: Array.from(this.calls.values()).map(c => ({
        id: c.id,
        state: c.state,
        callerId: c.callerId,
        callerName: c.callerName
      })),
      // Métricas de red
      networkMetrics: {
        currentLatency: this.networkMetrics.latency,
        averageLatency: this.networkMetrics.averageLatency.toFixed(0),
        lastSuccessfulPing: this.networkMetrics.lastSuccessfulPing,
        failedPings: this.networkMetrics.failedPings,
        totalPings: this.networkMetrics.totalPings,
        successRate: this.networkMetrics.totalPings > 0
          ? ((this.networkMetrics.totalPings - this.networkMetrics.failedPings) / this.networkMetrics.totalPings * 100).toFixed(1) + '%'
          : 'N/A'
      },
      // Información de conexión del navegador
      connectionInfo: connectionInfo,
      // Estado de auto-reconexión
      reconnectionInfo: {
        isAutoConnectEnabled: this.isAutoConnectEnabled,
        reconnectAttempts: this.reconnectAttempts,
        maxReconnectAttempts: this.maxReconnectAttempts
      },
      // Información del objeto cliente completo (para debugging)
      rawClient: clientAny
    };
  }

  /**
   * Obtiene información sobre la conexión de red del navegador
   */
  private getConnectionInfo() {
    const nav = navigator as any;
    const connection = nav.connection || nav.mozConnection || nav.webkitConnection;

    if (!connection) {
      return {
        available: false,
        message: 'API de Network Information no disponible'
      };
    }

    return {
      available: true,
      effectiveType: connection.effectiveType, // 'slow-2g', '2g', '3g', '4g'
      downlink: connection.downlink, // Mbps
      rtt: connection.rtt, // ms (round-trip time)
      saveData: connection.saveData, // boolean
      type: connection.type // 'wifi', 'cellular', 'ethernet', etc.
    };
  }

  /**
   * Imprime información de diagnóstico en consola
   */
  printDiagnostics() {
    console.log('🔍 === DIAGNÓSTICO DE TELNYX SIP ===');
    console.log(this.getDiagnosticInfo());
    console.log('🔍 === FIN DIAGNÓSTICO ===');
  }

  /**
   * Exporta diagnósticos completos en formato JSON
   */
  exportDiagnostics() {
    const diagnostics = {
      timestamp: new Date().toISOString(),
      diagnosticInfo: this.getDiagnosticInfo(),
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      onLine: navigator.onLine,
      cookieEnabled: navigator.cookieEnabled
    };

    const json = JSON.stringify(diagnostics, null, 2);
    console.log('📊 === EXPORTACIÓN DE DIAGNÓSTICOS ===');
    console.log(json);
    console.log('📊 === FIN EXPORTACIÓN ===');

    // Descargar como archivo
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `telnyx-diagnostics-${new Date().getTime()}.json`;
    a.click();
    URL.revokeObjectURL(url);

    return diagnostics;
  }

  /**
   * 🔥 Obtiene datos de usuario automáticamente cuando llega una llamada
   * Este método se llama automáticamente cuando se detecta una llamada entrante
   * y evita peticiones duplicadas mediante cache
   */
  private async fetchUserDataForCall(callerId: string) {
    // Si ya tenemos los datos en cache, no hacer nada
    if (this.userDataByNumber.has(callerId)) {
      console.log(`📞 [SipService] Datos de usuario ya en cache para ${callerId}`);
      return;
    }

    // Si ya estamos cargando este número, no hacer nada
    if (this.loadingNumbers.has(callerId)) {
      console.log(`📞 [SipService] Datos de usuario ya en proceso de carga para ${callerId}`);
      return;
    }

    // Marcar como en proceso de carga
    this.loadingNumbers.add(callerId);

    try {
      console.log(`🔍 [SipService] Obteniendo datos de usuario para ${callerId}`);
      const result = await this.rest.getUserJourneysByNumber(callerId);

      if (result.ok && result.response) {
        // Guardar datos en el mapa usando callerId como clave
        this.userDataByNumber.set(callerId, {
          user: result.response.user,
          journeys: result.response.journeys
        });

        // Actualizar el callerName en la llamada correspondiente
        const call = Array.from(this.calls.values()).find(c => c.callerId === callerId);
        if (call && result.response.user?.name) {
          call.callerName = `${result.response.user.name} - ${callerId}`;
          this.notifyCallsUpdate();
        }

        // Notificar a los suscriptores que hay datos nuevos
        this.userDataUpdate$.next(new Map(this.userDataByNumber));
        console.log(`✅ [SipService] Datos de usuario obtenidos para ${callerId}`);
      }
    } catch (error) {
      console.error(`❌ [SipService] Error obteniendo datos de usuario para ${callerId}:`, error);
    } finally {
      // Quitar de la lista de números en carga
      this.loadingNumbers.delete(callerId);
    }
  }

  /**
   * 🔥 Obtiene datos de usuario para un número específico
   * Si los datos no están en cache, los obtiene automáticamente
   * @param callerId - Número de teléfono en formato E.164
   * @returns Datos de usuario o undefined si no están disponibles
   */
  getUserData(callerId: string): { user?: UserSmall, journeys: Journey[] } | undefined {
    return this.userDataByNumber.get(callerId);
  }

  /**
   * 🔥 Fuerza la obtención de datos de usuario para un número específico
   * Útil cuando se necesita refrescar los datos
   * @param callerId - Número de teléfono en formato E.164
   */
  async refreshUserData(callerId: string) {
    // Limpiar cache y forzar recarga
    this.userDataByNumber.delete(callerId);
    this.queriedNumbers.delete(callerId);
    await this.fetchUserDataForCall(callerId);
  }

  /**
   * 🔥 Limpia el cache de datos de usuario para un número específico
   * Útil cuando se cuelga una llamada o se crea un servicio para que la próxima vez se vuelva a consultar
   * @param callerId - Número de teléfono en formato E.164
   */
  clearUserDataForNumber(callerId: string) {
    if (callerId) {
      try {
        this.userDataByNumber.delete(callerId);
        this.queriedNumbers.delete(callerId);
        this.loadingNumbers.delete(callerId);
        // Notificar a los suscriptores que los datos han cambiado
        this.userDataUpdate$.next(new Map(this.userDataByNumber));
        console.log(`🧹 [SipService] Cache limpiado para ${callerId}`);
      } catch (error) {
        console.error(`❌ [SipService] Error al limpiar cache para ${callerId}:`, error);
      }
    }
  }

  /**
   * Limpia los datos de usuario cuando se desconecta
   */
  private clearUserData() {
    this.userDataByNumber.clear();
    this.queriedNumbers.clear();
    this.loadingNumbers.clear();
    this.userDataUpdate$.next(new Map());
  }
}